// Anmeldung – gemeinsame Begriffe und Formen der HTTP-Routen für Server (kernel/auth, kernel/http) und Oberfläche
// (web/api, Anmeldeseiten). docs/anmeldung/SPEZIFIKATION.md §6, §8.1. Läuft unverändert in Browser und Node.
//
//   Regeln und Grenzen   PASSWORD_* · TOTP · MFA_* · RECOVERY_* · LOGIN_LOCK_STAGES · SECURITY_POLICY_* · RESET_TOKEN_*
//   Sätze                AUTH_TEXTS · passwordProblemText · AUTH_EVENT_LABELS · SESSION_ENDED_REASON_LABELS
//   Formate              normalizeTotpCode · normalizeRecoveryCode · formatRecoveryCode · buildOtpauthUri · formatTotpSecret
//   HTTP (Zod)           AUTH_ROUTES: je Route Methode, Pfad (unter /api), Rumpf und Antwort; zSessionSecurity
//
// Alle Zeitpunkte in Antworten der Anmelde-Routen sind ISO-Texte (wie die Sitzungsantwort) – keine Date-Objekte.
import { z } from 'zod'
import { brand } from './brand'
import { zId } from './ids'

// ---- Kennwort (§6.1) ---------------------------------------------------------------------------------------

/** Mindestlänge neuer Kennwörter (MERXO_PASSWORD_MIN_LENGTH, erlaubt 10–64); bestehende kürzere melden sich weiter an */
export const PASSWORD_MIN_LENGTH = { default: 12, min: 10, max: 64 } as const
export const PASSWORD_MAX_LENGTH = 200

/** Gründe, aus denen ein neues Kennwort abgelehnt wird (passwordRules.ts, POST /auth/password/check) */
export const PASSWORD_PROBLEMS = [
  'too_short',
  'too_long',
  'blocklisted',
  'contains_email',
  'contains_name',
  'contains_company',
  'common_word',
  'repeated_chars',
  'sequence',
  'keyboard_row',
  'repeated_word',
  'common_pattern',
] as const
export type PasswordProblemCode = (typeof PASSWORD_PROBLEMS)[number]
export interface PasswordProblem {
  code: PasswordProblemCode
  message: string
}

/** Satz zu einem Grund – deutsch, konkret, ohne Zeichenklassen-Vorschriften (NIST, §6.1) */
export function passwordProblemText(code: PasswordProblemCode, options: { minLength?: number } = {}): string {
  const min = options.minLength ?? PASSWORD_MIN_LENGTH.default
  switch (code) {
    case 'too_short':
      return `Das Kennwort ist zu kurz – bitte mindestens ${min} Zeichen. Ein Satz aus mehreren Wörtern ist leicht zu merken.`
    case 'too_long':
      return `Das Kennwort ist zu lang (höchstens ${PASSWORD_MAX_LENGTH} Zeichen).`
    case 'blocklisted':
      return 'Das Kennwort steht auf einer Liste häufig benutzter Kennwörter.'
    case 'contains_email':
      return 'Das Kennwort enthält Ihre E-Mail-Adresse.'
    case 'contains_name':
      return 'Das Kennwort enthält Ihren Namen.'
    case 'contains_company':
      return 'Das Kennwort enthält den Namen Ihres Betriebs.'
    case 'common_word':
      return `Das Kennwort besteht fast nur aus einem naheliegenden Wort wie „Kennwort“ oder „${brand.name}“.`
    case 'repeated_chars':
      return 'Das Kennwort wiederholt nur ein Zeichen.'
    case 'sequence':
      return 'Das Kennwort ist eine einfache Folge wie „123456…“ oder „abcdef…“.'
    case 'keyboard_row':
      return 'Das Kennwort folgt einer Tastaturreihe wie „qwertz…“.'
    case 'repeated_word':
      return 'Das Kennwort wiederholt nur ein Wort.'
    case 'common_pattern':
      return 'Das Kennwort folgt einem leicht zu erratenden Muster wie „Sommer2026!“ oder „qwertz123456“. Ein Satz aus mehreren eigenen Wörtern ist sicherer.'
  }
}

export const zPasswordProblem = z.object({ code: z.enum(PASSWORD_PROBLEMS), message: z.string() })

// ---- Zwei-Faktor (§6.5, §6.6) --------------------------------------------------------------------------------

/** TOTP nach RFC 6238: SHA-1, 6 Ziffern, 30 s, aktueller Schritt ±1 */
export const TOTP = { algorithm: 'SHA1', digits: 6, periodSeconds: 30, window: 1, secretBytes: 20 } as const

/** Challenge nach richtigem Kennwort: Gültigkeit, Versuche; Einrichtung: ausstehendes Geheimnis; Sperre je Person */
export const MFA_LIMITS = {
  challengeMinutes: 5,
  challengeAttempts: 5,
  setupPendingMinutes: 15,
  /** falsche Codes je Person und Stunde → Sperre und Hinweis-Mail (Zähler mfa:<Nutzer>) */
  failuresPerHour: 10,
  lockMinutes: 15,
} as const

/** Wiederherstellungscodes: 10 Stück, 12 Zeichen Crockford-Base32 (60 Bit), angezeigt ABCD-EFGH-JKMN */
export const RECOVERY_CODES = { count: 10, length: 12, alphabet: '0123456789ABCDEFGHJKMNPQRSTVWXYZ', lowWarning: 2 } as const

/** Code aus der Authenticator-App: Leerzeichen und Bindestriche erlaubt, genau 6 Ziffern – sonst null */
export function normalizeTotpCode(input: string): string | null {
  const digits = input.replace(/[\s-]/g, '')
  return new RegExp(`^\\d{${TOTP.digits}}$`).test(digits) ? digits : null
}

/** Wiederherstellungscode vereinheitlichen: ohne Bindestriche/Leerraum, groß, O→0, I/L→1, U ist nicht im Alphabet */
export function normalizeRecoveryCode(input: string): string | null {
  const cleaned = input
    .replace(/[\s-]/g, '')
    .toUpperCase()
    .replace(/O/g, '0')
    .replace(/[IL]/g, '1')
  if (cleaned.length !== RECOVERY_CODES.length) return null
  for (const ch of cleaned) if (!RECOVERY_CODES.alphabet.includes(ch)) return null
  return cleaned
}

/** 'ABCDEFGHJKMN' → 'ABCD-EFGH-JKMN' */
export const formatRecoveryCode = (code: string): string => code.replace(/(.{4})(?=.)/g, '$1-')

/** Geheimnis (Base32 ohne Füllzeichen) in Vierergruppen für die Anzeige */
export const formatTotpSecret = (secret: string): string => secret.replace(/(.{4})(?=.)/g, '$1 ')

/**
 * Einrichtungs-URI der Authenticator-Apps (Key-URI-Format):
 * otpauth://totp/<Aussteller>:<E-Mail>?secret=…&issuer=…&algorithm=SHA1&digits=6&period=30
 */
export function buildOtpauthUri(input: { issuer: string; account: string; secret: string }): string {
  const label = `${encodeURIComponent(input.issuer)}:${encodeURIComponent(input.account)}`
  const query = [
    `secret=${input.secret}`,
    `issuer=${encodeURIComponent(input.issuer)}`,
    `algorithm=${TOTP.algorithm}`,
    `digits=${TOTP.digits}`,
    `period=${TOTP.periodSeconds}`,
  ].join('&')
  return `otpauth://totp/${label}?${query}`
}

/** Anmeldemethoden einer Sitzung (session.amr, RFC 8176): Kennwort, Code, Wiederherstellungscode, vertrautes Gerät */
export const AMR_VALUES = ['pwd', 'otp', 'rec', 'device'] as const
export type AmrValue = (typeof AMR_VALUES)[number]

// ---- Fehlversuche und Sperren (§6.2) ------------------------------------------------------------------------

/** Stufen der Sperre je Adresse bzw. bekanntem Gerät: ab n Fehlversuchen im Fenster so viele Minuten */
export const LOGIN_LOCK_STAGES = [
  { failures: 5, lockMinutes: 1 },
  { failures: 8, lockMinutes: 5 },
  { failures: 12, lockMinutes: 15 },
  { failures: 16, lockMinutes: 60 },
] as const
export const LOGIN_FAILURE_WINDOW_HOURS = 24

/** Sperrdauer in Minuten nach `failures` Fehlversuchen (0 = keine Sperre) */
export function loginLockMinutes(failures: number): number {
  let minutes = 0
  for (const stage of LOGIN_LOCK_STAGES) if (failures >= stage.failures) minutes = stage.lockMinutes
  return minutes
}

/** Anträge „Kennwort vergessen“ je Adresse und Stunde – darüber still verworfen */
export const RESET_REQUESTS_PER_HOUR = 3
/** Freigabe-PIN: Versuche je Gerät im Fenster (wie bisher im Speicher, künftig in auth_throttle) */
export const SECOND_PERSON_DEVICE_LIMIT = { attempts: 10, windowMinutes: 5 } as const

/** Schlüssel in auth_throttle (§6.2). Prüfwerte statt Adressen: HMAC mit purposeKey('throttle'). */
export const THROTTLE_KEY_PREFIXES = ['acct', 'dev', 'mfa', 'reset', 'sp'] as const
export type ThrottleKeyPrefix = (typeof THROTTLE_KEY_PREFIXES)[number]

// ---- Richtlinie, Bestätigung, Token (§6.3, §6.4, §6.7) ------------------------------------------------------

/** tenant_security_policy: Standardwerte und erlaubter Bereich der Tage */
export const SECURITY_POLICY_DEFAULTS = { mfaRequiredAll: false, mfaGraceDays: 14, trustedDeviceDays: 30, handoverLinks: false, supportAccess: true } as const
export const SECURITY_POLICY_DAYS = { min: 0, max: 90 } as const

/** Frische Bestätigung für sicherheitsrelevante Aktionen (Dispatcher, effects.securitySensitive) */
export const REAUTH_MAX_AGE_MINUTES = 10

/** Rücksetz-Link (MERXO_RESET_TOKEN_MINUTES) */
export const RESET_TOKEN_MINUTES = { default: 30, min: 15, max: 120 } as const

/** „Gilt n Minuten“ – n aus MERXO_RESET_TOKEN_MINUTES (GET /api/auth/config → reset.tokenMinutes) */
export function resetValidityText(minutes: number = RESET_TOKEN_MINUTES.default): string {
  // geschütztes Leerzeichen: Zahl und Einheit brechen nicht auseinander („45 Minuten“ am Telefon)
  return minutes % 60 === 0 && minutes >= 60 ? (minutes === 60 ? 'eine Stunde' : `${minutes / 60}\u00a0Stunden`) : `${minutes}\u00a0Minuten`
}

/** Satz nach „Kennwort vergessen“ – für bekannte und unbekannte Adressen gleich */
export function forgotSentText(minutes: number = RESET_TOKEN_MINUTES.default): string {
  return `Wenn es zu dieser Adresse einen Zugang gibt, haben wir einen Link geschickt. Er gilt ${resetValidityText(minutes)}.`
}

/** Präfixe der Einmal-Token in Links (danach 32 Byte Zufall, base64url = 43 Zeichen) */
export const AUTH_TOKEN_PREFIXES = { passwordReset: 'mxr_', invitation: 'mxi_' } as const
export const zResetToken = z.string().regex(/^mxr_[A-Za-z0-9_-]{43}$/, { error: 'Der Link ist ungültig oder abgelaufen.' })
/** Einladungen: wie bisher (server/kernel/auth/members.ts) – Form großzügig, Prüfung über den Hash */
export const zInvitationToken = z.string().regex(/^[A-Za-z0-9_-]{20,200}$/, { error: 'Die Einladung ist ungültig oder abgelaufen.' })

/** details.reason in Fehlerantworten der Anmeldung und des Dispatchers */
export const AUTH_REASONS = {
  /** 401 nach richtigem Kennwort: Code verlangt (Challenge-Cookie gesetzt) */
  mfaRequired: 'mfa_required',
  /** 401 nach richtigem Kennwort: Pflicht nach Frist, Einrichtung ist der einzige Weg hinein */
  mfaSetupRequired: 'mfa_setup_required',
  /** 403 im Dispatcher: sicherheitsrelevante Aktion ohne frische Bestätigung dieser Sitzung */
  reauthRequired: 'reauth_required',
  /** 403: Verwaltung von Zugängen nur durch eine angemeldete Person, nicht per API-Schlüssel */
  personRequired: 'person_required',
  /** 403 beim Wählen eines Betriebs über den Support-Zugang (§18): Konto ohne Zwei-Faktor bzw. Sitzung ohne Code */
  supportMfaRequired: 'support_mfa_required',
  /** 403: Der Betrieb hat den Support-Zugang des Betreibers abgeschaltet (§18) */
  supportDisabled: 'support_disabled',
} as const
export type AuthReason = (typeof AUTH_REASONS)[keyof typeof AUTH_REASONS]

/** Fertige Sätze – für bekannte und unbekannte Adressen immer dieselben (keine Nutzer-Aufzählung) */
export const AUTH_TEXTS = {
  loginFailed: 'E-Mail-Adresse oder Kennwort sind nicht korrekt.',
  locked: 'Zu viele Anmeldeversuche. Bitte versuchen Sie es in einigen Minuten erneut.',
  codeWrong: 'Der Code stimmt nicht. Bitte geben Sie den aktuellen Code aus Ihrer App ein.',
  codeLocked: 'Zu viele falsche Codes. Bitte melden Sie sich erneut mit Ihrem Kennwort an.',
  challengeExpired: 'Die Anmeldung hat zu lange gedauert. Bitte geben Sie Ihr Kennwort erneut ein.',
  /** mit der Standard-Gültigkeit – die Seite nimmt forgotSentText(Minuten der Installation) */
  forgotSent: forgotSentText(RESET_TOKEN_MINUTES.default),
  resetInvalid: 'Der Link ist ungültig oder abgelaufen. Bitte fordern Sie einen neuen an.',
  resetCodeRequired: 'Für diesen Zugang ist die Zwei-Faktor-Anmeldung eingerichtet. Bitte geben Sie einen Code aus Ihrer App oder einen Wiederherstellungscode ein.',
  mfaTransferCodeRequired: 'Bitte geben Sie zuerst einen Code aus der App auf Ihrem bisherigen Telefon oder einen Wiederherstellungscode ein.',
  passwordChanged: 'Ihr Kennwort ist geändert.',
  currentPasswordWrong: 'Das bisherige Kennwort stimmt nicht.',
  demoUnavailable: 'Im Demo-Betrieb nicht verfügbar.',
  mfaRequiredByTenant: 'Ihr Betrieb verlangt die Zwei-Faktor-Anmeldung.',
  mfaResetOtherTenant:
    'Diese Person hat auch Zugang zu einem anderen Betrieb. Die Zwei-Faktor-Anmeldung kann nur der Betreiber zurücksetzen.',
  reauthRequired:
    'Zu Ihrer Sicherheit: Bitte bestätigen Sie diese Änderung mit Ihrem Kennwort. Ihre letzte Anmeldung liegt mehr als zehn Minuten zurück.',
  noSecretKey:
    'Auf dem Server ist kein Schlüssel hinterlegt (MERXO_SECRET_KEY) – Zwei-Faktor-Anmeldung und Kennwort-Links sind nicht verfügbar.',
  handoverLinksOff: 'Links zum Weitergeben sind in diesem Betrieb nicht erlaubt. Die E-Mail geht direkt an die Person.',
  // ---- Support-Zugang des Betreibers (§18) ----
  supportMfaRequired: 'Für den Support-Zugang braucht Ihr Konto die Zwei-Faktor-Anmeldung.',
  supportMfaSession:
    'Für den Support-Zugang braucht Ihr Konto die Zwei-Faktor-Anmeldung – diese Sitzung begann ohne Code (vertrautes Gerät). Bitte melden Sie sich ab und mit dem Code aus Ihrer App wieder an.',
  supportDisabled: 'Dieser Betrieb hat den Support-Zugang des Betreibers abgeschaltet.',
  supportManaged:
    'Der Support-Zugang des Betreibers lässt sich hier nicht ändern. Erlauben oder abschalten können Sie ihn unter Einstellungen › Sicherheit.',
  supportOwnersOnly: 'Den Support-Zugang des Betreibers erlaubt oder sperrt nur die Geschäftsführung des Betriebs (Rolle mit allen Rechten).',
  // neutral – verrät nicht, warum (z. B. eine Adresse des Betreiber-Supports, §18)
  invitationRefused: 'Diese Adresse kann nicht eingeladen werden.',
  invitationNotAcceptable: 'Diese Einladung kann nicht angenommen werden.',
  supportRestricted:
    'Über den Support-Zugang des Betreibers gibt es keine Kennwort-Links zum Weitergeben und kein Zurücksetzen der Zwei-Faktor-Anmeldung einer Person – das übernimmt die Geschäftsführung des Betriebs.',
} as const

/** Zusatz im Klartext jedes Audit-Eintrags, den der Betreiber über den Support-Zugang auslöst (§18) – Teil der Versiegelung */
export const SUPPORT_AUDIT_SUFFIX = ' – über den Support-Zugang des Betreibers'

/** Frist der Pflicht als Satz für die Leiste nach der Anmeldung */
export const mfaDueText = (dueDate: string): string => `Bitte richten Sie bis ${dueDate} die Zwei-Faktor-Anmeldung ein.`

// ---- Sitzungen und Sicherheitsprotokoll (§6.8, §6.12) -------------------------------------------------------

/** session.ended_reason */
export const SESSION_ENDED_REASONS = [
  'logout',
  'logout_all',
  'revoked_self',
  'revoked_admin',
  'password_changed',
  'password_reset',
  'mfa_changed',
  'membership_disabled',
] as const
export type SessionEndedReason = (typeof SESSION_ENDED_REASONS)[number]
export const SESSION_ENDED_REASON_LABELS: Record<SessionEndedReason, string> = {
  logout: 'Abgemeldet',
  logout_all: 'Auf allen Geräten abgemeldet',
  revoked_self: 'Von Ihnen beendet',
  revoked_admin: 'Von der Verwaltung beendet',
  password_changed: 'Kennwort geändert',
  password_reset: 'Kennwort zurückgesetzt',
  mfa_changed: 'Zwei-Faktor-Anmeldung geändert',
  membership_disabled: 'Zugang gesperrt',
}

/** Arten in auth_event (§6.12) – 'login.failed' nennt den Grund nur intern (detail.reason) */
export const AUTH_EVENT_KINDS = [
  'login.succeeded',
  'login.failed',
  'login.locked',
  'login.mfa_required',
  'mfa.succeeded',
  'mfa.failed',
  'mfa.locked',
  'mfa.setup_started',
  'mfa.enabled',
  'mfa.disabled',
  'mfa.replaced',
  'mfa.reset',
  'mfa.recovery_used',
  'mfa.recovery_regenerated',
  'device.new',
  'device.trusted',
  'device.forgotten',
  'session.revoked',
  'logout',
  'logout.everywhere',
  'reauth.succeeded',
  'reauth.failed',
  'password.changed',
  'password.reset_requested',
  'password.reset_completed',
  'password.reset_failed',
  'invitation.sent',
  'invitation.accepted',
  'second_person.failed',
  'second_person.locked',
  'notice.sent',
  // Support-Zugang des Betreibers (§18)
  'support.opened',
  'support.refused',
  'support.ended',
  'support.operator_added',
  'support.operator_removed',
] as const
export type AuthEventKind = (typeof AUTH_EVENT_KINDS)[number]

export const AUTH_EVENT_LABELS: Record<AuthEventKind, string> = {
  'login.succeeded': 'Angemeldet',
  'login.failed': 'Anmeldung fehlgeschlagen',
  'login.locked': 'Anmeldung vorübergehend gesperrt',
  'login.mfa_required': 'Kennwort richtig, Code verlangt',
  'mfa.succeeded': 'Code bestätigt',
  'mfa.failed': 'Falscher Code',
  'mfa.locked': 'Nach falschen Codes gesperrt',
  'mfa.setup_started': 'Einrichtung der Zwei-Faktor-Anmeldung begonnen',
  'mfa.enabled': 'Zwei-Faktor-Anmeldung eingeschaltet',
  'mfa.disabled': 'Zwei-Faktor-Anmeldung ausgeschaltet',
  'mfa.replaced': 'Zwei-Faktor-Anmeldung auf ein neues Gerät übertragen',
  'mfa.reset': 'Zwei-Faktor-Anmeldung zurückgesetzt',
  'mfa.recovery_used': 'Wiederherstellungscode benutzt',
  'mfa.recovery_regenerated': 'Neue Wiederherstellungscodes erzeugt',
  'device.new': 'Anmeldung von einem neuen Gerät',
  'device.trusted': 'Gerät als vertraut gespeichert',
  'device.forgotten': 'Gerät vergessen',
  'session.revoked': 'Sitzung beendet',
  logout: 'Abgemeldet',
  'logout.everywhere': 'Auf allen Geräten abgemeldet',
  'reauth.succeeded': 'Änderung bestätigt',
  'reauth.failed': 'Bestätigung fehlgeschlagen',
  'password.changed': 'Kennwort geändert',
  'password.reset_requested': 'Kennwort-Link angefordert',
  'password.reset_completed': 'Kennwort über Link neu festgelegt',
  'password.reset_failed': 'Kennwort-Link abgelehnt',
  'invitation.sent': 'Einladung verschickt',
  'invitation.accepted': 'Einladung angenommen',
  'second_person.failed': 'Freigabe-PIN falsch',
  'second_person.locked': 'Freigabe-PIN gesperrt',
  'notice.sent': 'Sicherheitshinweis per E-Mail verschickt',
  'support.opened': 'Support-Zugang des Betreibers geöffnet',
  'support.refused': 'Support-Zugang abgelehnt',
  'support.ended': 'Support-Zugang beendet',
  'support.operator_added': 'Konto für den Betreiber-Support freigegeben (Server)',
  'support.operator_removed': 'Konto aus dem Betreiber-Support genommen (Server)',
}
/** Form der Arten – auch in der Datenbank geprüft (auth_event_kind_check; „logout“ hat keinen Punkt) */
export const AUTH_EVENT_KIND_PATTERN = /^[a-z_]+(?:\.[a-z_]+)*$/

/** Anlässe der Kontoschreiben account.security (§5.7) – nicht abbestellbar */
export const SECURITY_NOTICE_KINDS = [
  'login_new_device',
  'password_changed',
  'password_reset',
  'mfa_enabled',
  'mfa_disabled',
  'mfa_replaced',
  'mfa_reset',
  'recovery_used',
  'recovery_regenerated',
  'mfa_failures',
] as const
export type SecurityNoticeKind = (typeof SECURITY_NOTICE_KINDS)[number]

// ---- Formen der HTTP-Routen (§8.1) – Rümpfe streng, Antworten mit ISO-Zeitpunkten ------------------------------

const zIso = z.string()
const zEmailInput = z.string().trim().min(3).max(320)
const zPasswordInput = z.string().min(1).max(PASSWORD_MAX_LENGTH)
/** TOTP- oder Wiederherstellungscode, wie eingegeben – der Server vereinheitlicht (normalizeTotpCode/…RecoveryCode) */
const zCodeInput = z.string().trim().min(6).max(32)

/** additiv in der Sitzungsantwort (GET /api/session, docs/API.md §3.1) */
export const zSessionSecurity = z.object({
  mfa: z.enum(['on', 'off']),
  /** gilt für die Person eine Pflicht (strengste Mitgliedschaft)? */
  mfaRequired: z.boolean(),
  /** Frist der Einrichtung (ISO) – nur solange die Pflicht gilt und 2FA fehlt */
  mfaSetupDueAt: zIso.nullable(),
  /** verbliebene Wiederherstellungscodes (null ohne 2FA) */
  recoveryCodesLeft: z.number().int().nonnegative().nullable(),
  failedLoginsSinceLast: z.number().int().nonnegative(),
})
export type SessionSecurity = z.infer<typeof zSessionSecurity>

/**
 * Support-Zugang des Betreibers (§18), additiv in der Sitzungsantwort – nur für Betreiber (freigegebenes Konto mit
 * Adresse in MERXO_SUPPORT_OPERATORS),
 * sonst fehlt das Feld (null). `tenants`: alle aktiven Betriebe, in denen die Person kein eigenes Mitglied ist.
 */
export const zSessionSupport = z.object({
  /** die Sitzung arbeitet gerade über den Support-Zugang (Hinweisbalken) */
  active: z.boolean(),
  /** Zugang möglich: Zwei-Faktor eingerichtet und diese Sitzung mit Code begonnen */
  available: z.boolean(),
  /** Satz, warum nicht (AUTH_TEXTS.supportMfaRequired bzw. supportMfaSession) – null, wenn verfügbar */
  reason: z.string().nullable(),
  tenants: z.array(
    z.object({
      tenantId: z.string(),
      tenantName: z.string(),
      tenantSlug: z.string(),
      demo: z.boolean(),
      /** der Betrieb erlaubt den Support-Zugang (Einstellungen › Sicherheit) */
      allowed: z.boolean(),
    }),
  ),
})
export type SessionSupport = z.infer<typeof zSessionSupport>

/** details der 401-Antwort nach richtigem Kennwort (Cookie merxo_mfa ist gesetzt) */
export const zLoginChallenge = z.discriminatedUnion('reason', [
  z.object({ reason: z.literal('mfa_required'), methods: z.array(z.enum(['totp', 'recovery'])), expiresAt: zIso }),
  z.object({ reason: z.literal('mfa_setup_required'), expiresAt: zIso }),
])
export type LoginChallenge = z.infer<typeof zLoginChallenge>

export const zLoginBody = z.strictObject({ email: zEmailInput, password: zPasswordInput, tenantId: zId.optional() })
export const zLoginMfaBody = z.strictObject({ code: zCodeInput, rememberDevice: z.boolean().default(false) })
export const zMfaSetupResult = z.object({ secret: z.string(), otpauthUri: z.string(), issuer: z.string(), account: z.string() })
export const zCodeBody = z.strictObject({ code: zCodeInput })
/** Einrichtung beginnen; ist die Zwei-Faktor-Anmeldung schon eingerichtet (Übertragen auf ein neues Gerät), Pflicht: Code des bisherigen Geräts oder Wiederherstellungscode */
export const zMfaSetupBody = z.strictObject({ code: zCodeInput.optional() })
export const zRecoveryCodesResult = z.object({ recoveryCodes: z.array(z.string()).length(RECOVERY_CODES.count) })
/** Bestätigen der Einrichtung; beim Pflicht-Ablauf (Challenge mfa_setup) entsteht dabei die Sitzung (`session`) */
export const zMfaConfirmResult = zRecoveryCodesResult.extend({ session: z.unknown().nullable() })
export const zReauthBody = z.union([z.strictObject({ password: zPasswordInput }), z.strictObject({ code: zCodeInput })])
export const zReauthResult = z.object({ reauthAt: zIso })
export const zChangePasswordBody = z.strictObject({
  current: zPasswordInput,
  next: zPasswordInput,
  endOtherSessions: z.boolean().default(true),
})
export const zChangePasswordResult = z.object({ ok: z.literal(true), endedSessions: z.number().int().nonnegative() })
export const zPasswordCheckBody = z.strictObject({ password: zPasswordInput, email: z.string().max(320).optional() })
export const zPasswordCheckResult = z.object({ ok: z.boolean(), problem: zPasswordProblem.nullable() })
export const zForgotPasswordBody = z.strictObject({ email: zEmailInput })
export const zOkResult = z.object({ ok: z.literal(true) })
export const zResetCheckBody = z.strictObject({ token: zResetToken })
export const zResetCheckResult = z.object({ valid: z.literal(true), emailMasked: z.string(), needsCode: z.boolean(), expiresAt: zIso })
export const zResetPasswordBody = z.strictObject({ token: zResetToken, password: zPasswordInput, code: zCodeInput.optional() })

export const zSessionInfo = z.object({
  id: zId,
  /** die Sitzung dieser Anfrage */
  current: z.boolean(),
  deviceLabel: z.string(),
  ip: z.string().nullable(),
  tenantName: z.string().nullable(),
  createdAt: zIso,
  lastSeenAt: zIso,
  expiresAt: zIso,
  /** mit zweitem Faktor bzw. vertrautem Gerät entstanden */
  mfa: z.boolean(),
  userDeviceId: zId.nullable(),
})
export const zDeviceInfo = z.object({
  id: zId,
  label: z.string(),
  current: z.boolean(),
  createdAt: zIso,
  lastSeenAt: zIso,
  lastIp: z.string().nullable(),
  trustedUntil: zIso.nullable(),
})
export const zSessionsResult = z.object({ sessions: z.array(zSessionInfo), devices: z.array(zDeviceInfo) })
export const zRevokeSessionBody = z.union([z.strictObject({ sessionId: zId }), z.strictObject({ others: z.literal(true) })])
export const zRevokeSessionResult = z.object({ revoked: z.number().int().nonnegative() })
export const zForgetDeviceBody = z.union([z.strictObject({ deviceId: zId }), z.strictObject({ all: z.literal(true) })])
export const zSecurityEventsQuery = z.strictObject({ limit: z.coerce.number().int().min(1).max(200).default(50) })
export const zSecurityEvent = z.object({
  id: zId,
  at: zIso,
  kind: z.string(),
  /** deutscher Satz (AUTH_EVENT_LABELS) */
  label: z.string(),
  ip: z.string().nullable(),
  deviceLabel: z.string().nullable(),
  tenantName: z.string().nullable(),
  /** ohne Geheimnisse; nur Felder, die die Oberfläche in Worten zeigt (z. B. { remaining: 3 }) */
  detail: z.record(z.string(), z.unknown()),
})
export const zSecurityEventsResult = z.object({ events: z.array(zSecurityEvent) })
export const zInvitationInfoBody = z.strictObject({ token: zInvitationToken })
export const zInvitationInfoResult = z.object({ tenantName: z.string(), email: z.string(), displayName: z.string(), expiresAt: zIso })
export const zInvitationAcceptBody = z.strictObject({ token: zInvitationToken, password: zPasswordInput.optional() })
export const zInvitationAcceptResult = z.object({ tenantId: zId, email: z.string(), createdAccount: z.boolean() })

/**
 * Die Anmelde-Routen des Kerns (§8.1), Pfade unter /api. `body` prüft der Server mit parseBody, `result` ist der Inhalt
 * von `{ result }`. Alle Antworten tragen `Cache-Control: no-store`; ändernde Routen verlangen den CSRF-Kopf.
 * `session: true` = nur mit Sitzung; `fresh: true` = zusätzlich frische Bestätigung (oder die passende Challenge).
 */
export const AUTH_ROUTES = {
  login: { method: 'POST', path: '/auth/login', body: zLoginBody, result: null, session: false },
  loginMfa: { method: 'POST', path: '/auth/login/mfa', body: zLoginMfaBody, result: null, session: false },
  mfaSetup: { method: 'POST', path: '/auth/mfa/setup', body: zMfaSetupBody, result: zMfaSetupResult, session: false },
  mfaConfirm: { method: 'POST', path: '/auth/mfa/confirm', body: zCodeBody, result: zMfaConfirmResult, session: false },
  mfaDisable: { method: 'POST', path: '/auth/mfa/disable', body: zCodeBody, result: zOkResult, session: true, fresh: true },
  recoveryCodes: { method: 'POST', path: '/auth/mfa/recovery-codes', body: zCodeBody, result: zRecoveryCodesResult, session: true, fresh: true },
  reauth: { method: 'POST', path: '/auth/reauth', body: zReauthBody, result: zReauthResult, session: true },
  changePassword: { method: 'POST', path: '/auth/password', body: zChangePasswordBody, result: zChangePasswordResult, session: true },
  passwordCheck: { method: 'POST', path: '/auth/password/check', body: zPasswordCheckBody, result: zPasswordCheckResult, session: false },
  forgotPassword: { method: 'POST', path: '/auth/password/forgot', body: zForgotPasswordBody, result: zOkResult, session: false },
  resetCheck: { method: 'POST', path: '/auth/password/reset/check', body: zResetCheckBody, result: zResetCheckResult, session: false },
  resetPassword: { method: 'POST', path: '/auth/password/reset', body: zResetPasswordBody, result: zOkResult, session: false },
  sessions: { method: 'GET', path: '/auth/sessions', body: null, result: zSessionsResult, session: true },
  revokeSession: { method: 'POST', path: '/auth/sessions/revoke', body: zRevokeSessionBody, result: zRevokeSessionResult, session: true },
  forgetDevice: { method: 'POST', path: '/auth/devices/forget', body: zForgetDeviceBody, result: zOkResult, session: true },
  securityEvents: { method: 'GET', path: '/auth/events', body: null, result: zSecurityEventsResult, session: true },
  invitationInfo: { method: 'POST', path: '/auth/invitation/info', body: zInvitationInfoBody, result: zInvitationInfoResult, session: false },
  acceptInvitation: { method: 'POST', path: '/auth/invitation/accept', body: zInvitationAcceptBody, result: zInvitationAcceptResult, session: false },
} as const

/** Cookie der Challenge nach richtigem Kennwort (5 min, SameSite=Strict, Path=/api/auth) */
export const MFA_CHALLENGE_COOKIE = `${brand.storagePrefix}_mfa`

export type LoginBody = z.input<typeof zLoginBody>
export type LoginMfaBody = z.input<typeof zLoginMfaBody>
export type MfaSetupResult = z.infer<typeof zMfaSetupResult>
export type MfaConfirmResult<S = unknown> = { recoveryCodes: string[]; session: S | null }
export type ReauthBody = z.input<typeof zReauthBody>
export type ChangePasswordBody = z.input<typeof zChangePasswordBody>
export type ChangePasswordResult = z.infer<typeof zChangePasswordResult>
export type PasswordCheckResult = z.infer<typeof zPasswordCheckResult>
export type ResetCheckResult = z.infer<typeof zResetCheckResult>
export type ResetPasswordBody = z.input<typeof zResetPasswordBody>
export type SessionInfo = z.infer<typeof zSessionInfo>
export type DeviceInfo = z.infer<typeof zDeviceInfo>
export type SessionsResult = z.infer<typeof zSessionsResult>
export type RevokeSessionBody = z.input<typeof zRevokeSessionBody>
export type ForgetDeviceBody = z.input<typeof zForgetDeviceBody>
export type SecurityEvent = z.infer<typeof zSecurityEvent>
export type InvitationInfo = z.infer<typeof zInvitationInfoResult>
export type InvitationAcceptBody = z.input<typeof zInvitationAcceptBody>
export type InvitationAcceptResult = z.infer<typeof zInvitationAcceptResult>
